import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import LoginModal from './LoginModal';
import WithdrawModal from './WithdrawModal';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallToast, setShowInstallToast] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { getTotalItems } = useCart();
  const { member, isLoggedIn, logout } = useAuth();
  const cartItemCount = getTotalItems();

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);

    // PWA 설치 프롬프트 이벤트 캡처
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navItems = [
    { label: '제품소개', path: '/products' },
    { label: '브랜드 스토리', path: '/story' },
    { label: '고객후기', path: '/reviews' },
    { label: '포인트 구매', path: '/point-purchase' },
    { label: '내 포인트', path: '/my-points' },
    { label: '고객지원', path: '/support' },
    { label: '주문 내역', path: '/order-history' },
  ];

  const handleSignupNav = () => {
    if (isHome) {
      document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  const handleInstall = async () => {
    if (!installPrompt) {
      setShowInstallToast(true);
      setTimeout(() => setShowInstallToast(false), 4000);
      return;
    }
    await installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setMobileOpen(false);
  };

  const textColor = scrolled || !isHome ? 'text-gray-800' : 'text-white';
  const logoColor = scrolled || !isHome ? 'text-gray-900' : 'text-white';
  const hamburgerColor = scrolled || !isHome ? 'text-gray-800' : 'text-white';

  return (
    <>
      {/* 설치 안내 토스트 */}
      {showInstallToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] bg-gray-900 text-white text-sm px-5 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <span className="w-4 h-4 flex items-center justify-center text-emerald-400">
            <i className="ri-information-line"></i>
          </span>
          브라우저 주소창 옆 설치 아이콘(⊕)을 클릭하거나 이미 설치되어 있습니다
        </div>
      )}

      {/* 로그인 모달 */}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

      {/* 회원탈퇴 모달 */}
      {showWithdrawModal && <WithdrawModal onClose={() => setShowWithdrawModal(false)} />}

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-5 md:px-8 lg:px-12 py-4 md:py-5 flex items-center justify-between">
          <Link
            to="/"
            className={`flex items-center gap-2 md:gap-2.5 transition-all duration-500 cursor-pointer hover:opacity-80 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/752cfab177f04fff4e26527e73146f5a.jpg"
                alt="콩이사랑 로고"
                title="콩이사랑 브랜드 로고"
                className="w-full h-full object-cover"
              />
            </div>
            <h1
              className={`text-lg md:text-xl font-bold ${logoColor}`}
              style={{ fontFamily: 'Noto Serif KR, serif' }}
            >
              콩이사랑
            </h1>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-all duration-300 hover:text-emerald-500 cursor-pointer relative group ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                } ${textColor}`}
                style={{ transitionDelay: `${(index + 1) * 50}ms` }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-500 group-hover:w-full transition-all duration-300"></span>
              </Link>
            ))}

            <Link
              to="/cart"
              className={`relative w-10 h-10 flex items-center justify-center cursor-pointer hover:text-emerald-500 transition-all duration-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              } ${textColor}`}
              style={{ transitionDelay: '200ms' }}
              aria-label="장바구니"
            >
              <i className="ri-shopping-cart-line text-xl w-5 h-5 flex items-center justify-center"></i>
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </Link>

            {/* USDT 결제 바로가기 */}
            <Link
              to="/point-purchase"
              title="USDT로 결제하기"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-teal-400 text-teal-600 hover:bg-teal-400 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              } ${scrolled || !isHome ? '' : 'border-teal-300 text-teal-200 hover:bg-teal-400 hover:text-white hover:border-teal-400'}`}
              style={{ transitionDelay: '210ms' }}
              aria-label="USDT 결제"
            >
              <i className="ri-bit-coin-line text-sm w-4 h-4 flex items-center justify-center"></i>
              <span className="text-xs font-black tracking-wide">USDT</span>
            </Link>

            {/* 바탕화면에 추가 버튼 */}
            <button
              onClick={handleInstall}
              title="바탕화면에 추가 - 승유코퍼레이션"
              className={`w-10 h-10 flex items-center justify-center cursor-pointer hover:text-emerald-500 transition-all duration-300 ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              } ${textColor}`}
              style={{ transitionDelay: '220ms' }}
              aria-label="바탕화면에 추가"
            >
              <i className="ri-install-line text-xl w-5 h-5 flex items-center justify-center"></i>
            </button>

            {/* 로그인/회원 상태 버튼 */}
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 border-emerald-500 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                  } ${
                    scrolled || !isHome
                      ? 'text-emerald-600 hover:bg-emerald-50'
                      : 'text-white border-white/60 hover:bg-white/20'
                  }`}
                  style={{ transitionDelay: '230ms' }}
                >
                  <i className="ri-user-fill text-sm w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-sm font-semibold">{member?.member_name}</span>
                  <i className="ri-arrow-down-s-line text-xs w-3 h-3 flex items-center justify-center"></i>
                </button>

                {/* 드롭다운 메뉴 */}
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-gray-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-bold text-gray-900">{member?.member_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">ID: {member?.member_id}</p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <i className="ri-coin-line text-amber-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                          <span className="text-xs font-semibold text-amber-600">
                            {(member?.points_balance ?? 0).toLocaleString()}P
                          </span>
                        </div>
                      </div>
                      <Link
                        to="/my-points"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-coin-line w-4 h-4 flex items-center justify-center"></i>
                        내 포인트
                      </Link>
                      <Link
                        to="/order-history"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center"></i>
                        주문 내역
                      </Link>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          document.getElementById('discount')?.scrollIntoView({ behavior: 'smooth' });
                          if (!isHome) {
                            navigate('/');
                            setTimeout(() => {
                              document.getElementById('discount')?.scrollIntoView({ behavior: 'smooth' });
                            }, 500);
                          }
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-coupon-3-line w-4 h-4 flex items-center justify-center"></i>
                        할인 코드 사용
                      </button>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-logout-box-line w-4 h-4 flex items-center justify-center"></i>
                          로그아웃
                        </button>
                        <button
                          onClick={() => { setShowUserMenu(false); setShowWithdrawModal(true); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-user-unfollow-line w-4 h-4 flex items-center justify-center"></i>
                          회원 탈퇴
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* 회원가입 버튼 */}
                <button
                  onClick={handleSignupNav}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold border-2 border-emerald-500 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                  } ${
                    scrolled || !isHome
                      ? 'text-emerald-600 hover:bg-emerald-600 hover:text-white'
                      : 'text-white border-white/60 hover:bg-white/20'
                  }`}
                  style={{ transitionDelay: '230ms' }}
                >
                  회원가입
                </button>

                {/* 로그인 버튼 */}
                <button
                  onClick={() => setShowLoginModal(true)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                  }`}
                  style={{ transitionDelay: '240ms' }}
                >
                  로그인
                </button>
              </>
            )}

            <a
              href="#products"
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault();
                  document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer ${
                loaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
              }`}
              style={{ transitionDelay: '250ms' }}
            >
              구매하기
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${hamburgerColor}`}
            aria-label="메뉴 열기"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl w-6 h-6 flex items-center justify-center`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 z-50 w-72 h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-base font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>메뉴</span>
          <button
            onClick={() => setMobileOpen(false)}
            className="w-10 h-10 flex items-center justify-center cursor-pointer text-gray-600"
            aria-label="메뉴 닫기"
          >
            <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
          </button>
        </div>

        {/* 모바일 로그인 상태 표시 */}
        {isLoggedIn && member && (
          <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-200 rounded-full flex-shrink-0">
                <i className="ri-user-fill text-emerald-700 text-base w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{member.member_name} 님</p>
                <div className="flex items-center gap-1 mt-0.5">
                  <i className="ri-coin-line text-amber-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                  <span className="text-xs text-amber-600 font-semibold">{member.points_balance.toLocaleString()}P</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col px-5 py-6 gap-1 overflow-y-auto h-[calc(100%-80px)]">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className="text-base font-medium text-gray-700 hover:text-emerald-600 py-3 px-3 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/cart"
            onClick={() => setMobileOpen(false)}
            className="text-base font-medium text-gray-700 hover:text-emerald-600 py-3 px-3 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer flex items-center justify-between"
          >
            <span>장바구니</span>
            {cartItemCount > 0 && (
              <span className="w-6 h-6 bg-emerald-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cartItemCount > 99 ? '99+' : cartItemCount}
              </span>
            )}
          </Link>

          {/* 모바일 USDT 결제 바로가기 */}
          <Link
            to="/point-purchase"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 py-3 px-3 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors cursor-pointer border border-teal-200"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
              <i className="ri-bit-coin-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-teal-700 leading-tight">USDT 결제</p>
              <p className="text-xs text-teal-500 leading-tight">암호화폐로 바로 구매</p>
            </div>
            <i className="ri-arrow-right-line text-teal-400 w-4 h-4 flex items-center justify-center"></i>
          </Link>

          {/* 모바일 바탕화면 추가 버튼 */}
          <button
            onClick={() => { setMobileOpen(false); handleInstall(); }}
            className="text-base font-medium text-gray-700 hover:text-emerald-600 py-3 px-3 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer flex items-center gap-2"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-install-line"></i>
            </span>
            바탕화면에 추가
          </button>

          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col gap-2">
            {isLoggedIn ? (
              <>
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => {
                      if (!isHome) {
                        navigate('/');
                        setTimeout(() => document.getElementById('discount')?.scrollIntoView({ behavior: 'smooth' }), 500);
                      } else {
                        document.getElementById('discount')?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 300);
                  }}
                  className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold border-2 border-amber-400 text-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  할인 코드 사용
                </button>
                <button
                  onClick={handleLogout}
                  className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold border-2 border-red-300 text-red-500 hover:bg-red-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  로그아웃
                </button>
                <button
                  onClick={() => { setMobileOpen(false); setTimeout(() => setShowWithdrawModal(true), 300); }}
                  className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold border-2 border-gray-200 text-gray-400 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  회원 탈퇴
                </button>
              </>
            ) : (
              <>
                {/* 모바일 회원가입 버튼 */}
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => handleSignupNav(), 300);
                  }}
                  className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  회원가입
                </button>
                {/* 모바일 로그인 버튼 */}
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setTimeout(() => setShowLoginModal(true), 300);
                  }}
                  className="block w-full text-center px-6 py-3 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  로그인
                </button>
              </>
            )}
            <a
              href="#products"
              onClick={(e) => {
                setMobileOpen(false);
                if (isHome) {
                  e.preventDefault();
                  setTimeout(() => {
                    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                  }, 300);
                }
              }}
              className="block text-center px-6 py-3 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all whitespace-nowrap cursor-pointer"
            >
              구매하기
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
